import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, User as UserIcon, Loader2, KeyRound } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
interface AuthFormProps {
  mode: 'login' | 'register' | 'forgot';
  switchMode: (mode: 'login' | 'register' | 'forgot') => void;
}
export function AuthForm({
  mode,
  switchMode
}: AuthFormProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    login,
    register,
    resetPassword
  } = useAuth();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setIsSubmitting(true);
    try {
      if (mode === 'forgot') {
        if (!email) {
          setError('Por favor ingresa tu correo electrónico.');
          setIsSubmitting(false);
          return;
        }
        const res = await resetPassword(email);
        if (res.success) {
          setSuccessMsg(res.message);
        } else {
          setError(res.error || 'Error al enviar el correo.');
        }
      } else if (mode === 'register') {
        if (!name || !email || !password || !accessCode) {
          setError('Por favor completa todos los campos.');
          setIsSubmitting(false);
          return;
        }
        const res = await register(name, email, password, accessCode);
        if (!res.success) {
          setError(res.error || 'Error al registrarse.');
        } else if (res.needsConfirmation) {
          setSuccessMsg(res.message);
        }
      } else {
        if (!email || !password) {
          setError('Por favor ingresa tu correo y contraseña.');
          setIsSubmitting(false);
          return;
        }
        const res = await login(email, password);
        if (!res.success) {
          setError(res.error || 'Correo o contraseña incorrectos.');
        }
      }
    } catch (err) {
      setError('Ocurrió un error inesperado.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleSwitchMode = (newMode: 'login' | 'register' | 'forgot') => {
    switchMode(newMode);
    setError('');
    setSuccessMsg('');
  };
  const getButtonText = () => {
    if (mode === 'forgot') return 'Enviar enlace';
    if (mode === 'register') return 'Crear Cuenta';
    return 'Iniciar Sesión';
  };
  return <div className="bg-[#0c0c0e]/80 backdrop-blur-xl border border-white/10 rounded-3xl p-6 sm:p-8 shadow-[0_0_40px_rgba(0,0,0,0.5)] relative overflow-hidden" data-id="element-20">
      {/* Subtle top border glow */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-gold/50 to-transparent opacity-50" data-id="element-21" />

      {/* Mode Toggle — only show for login/register */}
      {mode !== 'forgot' && <div className="flex p-1 bg-white/5 rounded-xl mb-8" data-id="element-22">
          <button onClick={() => handleSwitchMode('login')} className={`flex-1 py-2.5 text-xs sm:text-sm font-bold rounded-lg transition-all ${mode === 'login' ? 'bg-white/10 text-white shadow-sm' : 'text-white/40 hover:text-white/70'}`} data-id="element-23">
            Iniciar Sesión
          </button>
          <button onClick={() => handleSwitchMode('register')} className={`flex-1 py-2.5 text-xs sm:text-sm font-bold rounded-lg transition-all ${mode === 'register' ? 'bg-white/10 text-white shadow-sm' : 'text-white/40 hover:text-white/70'}`} data-id="element-24">
            Registrarse
          </button>
        </div>}

      <AnimatePresence mode="wait" data-id="element-25">
        <motion.form key={mode} initial={{
        opacity: 0,
        x: mode === 'login' ? -20 : 20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: mode === 'login' ? 20 : -20
      }} transition={{
        duration: 0.3
      }} onSubmit={handleSubmit} className="space-y-4" data-id="element-26">
          {mode === 'register' && <div data-id="element-27">
              <label className="block text-white/60 text-xs font-bold uppercase tracking-wider mb-2 ml-1" data-id="element-28">
                Nombre completo
              </label>
              <div className="relative" data-id="element-29">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-30">
                  <UserIcon className="h-4 w-4 text-white/40" data-id="element-31" />
                </div>
                <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl py-3.5 pl-11 pr-4 text-white placeholder:text-white/20 focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/50 transition-all" placeholder="Tu nombre" disabled={isSubmitting} data-id="element-32" />
              </div>
            </div>}

          <div data-id="element-33">
            <label className="block text-white/60 text-xs font-bold uppercase tracking-wider mb-2 ml-1" data-id="element-34">
              Correo electrónico
            </label>
            <div className="relative" data-id="element-35">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-36">
                <Mail className="h-4 w-4 text-white/40" data-id="element-37" />
              </div>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl py-3.5 pl-11 pr-4 text-white placeholder:text-white/20 focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/50 transition-all" placeholder="tu@email.com" disabled={isSubmitting} data-id="element-38" />
            </div>
          </div>

          {mode !== 'forgot' && <div data-id="element-39">
              <label className="block text-white/60 text-xs font-bold uppercase tracking-wider mb-2 ml-1" data-id="element-40">
                Contraseña
              </label>
              <div className="relative" data-id="element-41">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-42">
                  <Lock className="h-4 w-4 text-white/40" data-id="element-43" />
                </div>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl py-3.5 pl-11 pr-4 text-white placeholder:text-white/20 focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/50 transition-all" placeholder="••••••••" disabled={isSubmitting} data-id="element-44" />
              </div>
            </div>}

          {mode === 'register' && <div data-id="element-45">
              <label className="block text-white/60 text-xs font-bold uppercase tracking-wider mb-2 ml-1" data-id="element-46">
                Código de acceso
              </label>
              <div className="relative" data-id="element-47">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-48">
                  <KeyRound className="h-4 w-4 text-white/40" data-id="element-49" />
                </div>
                <input type="text" value={accessCode} onChange={e => setAccessCode(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl py-3.5 pl-11 pr-4 text-white placeholder:text-white/20 focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/50 transition-all uppercase tracking-widest" placeholder="XXXX-XXXX-XXXX" disabled={isSubmitting} data-id="element-50" />
              </div>
              <p className="text-white/30 text-[10px] mt-1.5 ml-1" data-id="element-51">
                Incluido en tu compra. Revisa tu correo de confirmación.
              </p>
            </div>}

          {error && <motion.p initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-red-400 text-xs font-medium text-center bg-red-400/10 py-2 rounded-lg border border-red-400/20" data-id="element-52">
              {error}
            </motion.p>}

          {successMsg && <motion.p initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-green-400 text-xs font-medium text-center bg-green-400/10 py-2 px-3 rounded-lg border border-green-400/20" data-id="element-53">
              {successMsg}
            </motion.p>}

          <button type="submit" disabled={isSubmitting} className="w-full bg-gold text-dark font-black text-sm py-4 rounded-xl shadow-[0_5px_20px_rgba(212,175,55,0.25)] hover:bg-yellow-400 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-70 disabled:pointer-events-none flex items-center justify-center gap-2 mt-2 uppercase tracking-wider" data-id="element-54">
            {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-55" /> : getButtonText()}
          </button>

          {/* Forgot password link — only on login */}
          {mode === 'login' && <button type="button" onClick={() => handleSwitchMode('forgot')} className="w-full text-center text-white/40 hover:text-gold text-xs transition-colors mt-2" data-id="element-56">
              ¿Olvidaste tu contraseña?
            </button>}
        </motion.form>
      </AnimatePresence>

      <p className="text-center text-white/20 text-[10px] mt-6" data-id="element-57">
        🔒 Conexión segura con Supabase Auth
      </p>
    </div>;
}